import React from 'react';

import type { FactListItemProps, FactListProps } from './FactList.types';

/**
 * One label/value pair.
 *
 * The label holds its width and the value is right-aligned, so a long value
 * wraps against the right edge instead of crushing the label — the behaviour
 * two of the five hand-rolled copies had and three did not.
 */
export function FactListItem({
  label,
  mono = false,
  children,
  'data-testid': testId,
}: FactListItemProps): React.JSX.Element {
  return (
    <div
      className="flex items-center justify-between gap-3 text-body-sm"
      data-testid={testId}
    >
      <dt className="flex-none text-neutral-500">{label}</dt>
      <dd
        className={`text-right font-medium text-neutral-200 ${
          mono ? 'font-mono' : ''
        }`.trim()}
      >
        {children}
      </dd>
    </div>
  );
}

/**
 * FactList - the label/value rows in a detail page's about card.
 *
 * Renders a real `<dl>`: a fact is a term and its definition, which is what
 * the element is for. Four of the five about cards had built this out of
 * `<div><span><span>` with a private `Fact` component each — identical apart
 * from whether they bothered with `gap-3` and right-alignment.
 *
 * @example
 * <FactList>
 *   <FactList.Item label="Difficulty">{plan.difficulty}</FactList.Item>
 *   <FactList.Item label="Total workouts" mono>{count}</FactList.Item>
 *   {lastTrained && (
 *     <FactList.Item label="Last trained" mono>{lastTrained}</FactList.Item>
 *   )}
 * </FactList>
 */
export function FactList({
  children,
  className = '',
  'data-testid': testId,
}: FactListProps): React.JSX.Element {
  return (
    <dl
      className={`flex flex-col gap-3 ${className}`.trim()}
      data-testid={testId}
    >
      {children}
    </dl>
  );
}

FactList.Item = FactListItem;

export default FactList;
